import { FC, ReactElement, useState } from 'react';
import * as Yup from 'yup';
import i18next from 'i18next';
import { EntityScope } from '../../../../shared/utils/enums';
import { ExerciseTagDto } from '../../../../shared/dtos/exerciseTag.dto';
import { ExerciseCategoryDto } from '../../../../shared/dtos/exerciseCategory.dto';
import { ExerciseTagRequest } from '../../../../shared/requests/exerciseTag.request';
import { Alert, Button, Form, Spinner, Stack } from 'react-bootstrap';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { HttpMethod, useAuthFetchFromRestAPI } from '../../util/api';
import { Typeahead } from 'react-bootstrap-typeahead';

type FormValues = {
  germanTitle: string;
  englishTitle: string;
  categoryId: string;
  isActive: boolean;
}

type ExerciseTagFormProps = {
  initialData?: ExerciseTagDto;
  entityScope: EntityScope;
  categories: ExerciseCategoryDto[];
  onClose: () => void;
  onSubmit: (exerciseTag: ExerciseTagDto) => void;
}

const ExerciseTagForm: FC<ExerciseTagFormProps> = ({
  initialData,
  entityScope,
  categories,
  onSubmit,
  onClose
}: ExerciseTagFormProps): ReactElement => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { authFetch } = useAuthFetchFromRestAPI();

  const initialValues: FormValues = {
    germanTitle: initialData?.de ?? '',
    englishTitle: initialData?.en ?? '',
    categoryId: initialData?.categoryId ?? '',
    isActive: initialData?.isActive ?? true,
  };

  const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
    germanTitle: Yup.string().required(i18next.t('generic.required')),
    englishTitle: Yup.string().required(i18next.t('generic.required')),
    categoryId: Yup.string().required(i18next.t('generic.required')),
    isActive: Yup.boolean().default(true),
  });

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);

    try {

      const payload: ExerciseTagRequest = createInput(values);
      const result: ExerciseTagDto = await submitData(payload);
      onSubmit(result);

    } catch (error: any) {
      console.error(error);
      const msg: string = error?.message ?? (typeof error === 'string' ? error : i18next.t('generic.unknownError'));
      setErrorMessage(msg);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  const createInput = (values: FormValues): ExerciseTagRequest => {
    const category: ExerciseCategoryDto | undefined = categories
      .find((category: ExerciseCategoryDto) => category.id === values.categoryId);

    if (!category) {
      throw new Error('Category not found');
    }

    return {
      de: values.germanTitle.trim(),
      en: values.englishTitle.trim(),
      isActive: values.isActive,
      categoryId: category.id
    };
  };

  const submitData = async (payload: ExerciseTagRequest): Promise<ExerciseTagDto> => {
    const scope: string = entityScope === EntityScope.CORE ? 'core' : 'tenant';

    const url: string = initialData
      ? `/api/v1/exercise/tag/${scope}/${encodeURIComponent(initialData.id)}`
      : `/api/v1/exercise/tag/${scope}`;

    const method: HttpMethod = initialData ? 'PATCH' : 'POST';

    return authFetch(url, { method: method, body: payload });
  };


  return (
    <div className="position-relative">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}

      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ values, touched, errors, handleSubmit, handleChange, handleBlur, isSubmitting, setFieldValue }: FormikProps<FormValues>) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            className="p-2"
            style={{ opacity: isSaving ? 0.5 : 1 }}>
            <Stack gap={3}>
              <Form.Group controlId="germanTitle">
                <Form.Label>
                  {i18next.t('exerciseCategory.germanTitle')}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="germanTitle"
                  value={values.germanTitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.germanTitle && !!errors.germanTitle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.germanTitle}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="englishTitle">
                <Form.Label>
                  {i18next.t('exerciseCategory.englishTitle')}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="englishTitle"
                  value={values.englishTitle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.englishTitle && !!errors.englishTitle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.englishTitle}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="categoryId">
                <Form.Label>{i18next.t('exerciseTag.category')}</Form.Label>
                <Typeahead
                  id="category-typeahead"
                  labelKey={(option) => {
                    const category = option as ExerciseCategoryDto;

                    return `${i18next.language === 'de' ? category.de : category.en}${
                      category.scope === EntityScope.CORE ? ' (Core)' : ''
                    }`;
                  }}
                  options={categories}
                  selected={values.categoryId
                    ? categories.filter((category: ExerciseCategoryDto) => category.id === values.categoryId)
                    : []
                  }
                  onChange={(selected) => {
                    const category = selected[0] as ExerciseCategoryDto;

                    void setFieldValue('categoryId', selected.length > 0 ? category.id : '')
                  }}                
                  clearButton
                  disabled={categories.length === 0}>
                </Typeahead>
                {touched.categoryId && errors.categoryId && (
                  <div className="invalid-feedback d-block">
                    {errors.categoryId}
                  </div>
                )}
                {categories.length === 0 && (
                  <div className="form-text">
                    {i18next.t('exerciseTagForm.noCategories')}
                  </div>
                )}

              </Form.Group>

              <Form.Group controlId="isActive">
                <Form.Check
                  type="checkbox"
                  name="isActive"
                  label={i18next.t('exerciseCategory.isActive')}
                  checked={values.isActive}
                  onChange={handleChange}
                />
              </Form.Group>

              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting || isSaving}>
                  {i18next.t('generic.close')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting || isSaving || categories.length === 0}>
                  {i18next.t(initialData ? 'generic.save' : 'generic.create')}
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ExerciseTagForm;
